import {
    toTemporalZonedDateTime,
    startOf,
    endOf,
    formatDate,
    toDate as temporalToDate,
} from "../shared/lib/temporal";
import { useQueries } from "@tanstack/react-query";
import { useApiClient } from "../providers/ApiClientProvider";
import { Device } from "../types/Device";
import { Video } from "../types/Video";

interface UseFetchDeviceVideosOptions {
    devices: Device[];
    selectedDate: Date;
    timezone: string;
    enabled?: boolean;
}

// Aggregate videos by hour
const aggregateVideosByHour = (videos: Video[], timezone: string) => {
    const hourlyVideos: Record<string, Video[]> = {};

    videos.forEach((video) => {
        try {
            const videoTime = toTemporalZonedDateTime(
                new Date(video.time),
                timezone,
            );
            const hourKey = formatDate(
                startOf(videoTime, "hour"),
                "YYYY-MM-DD HH:mm",
            );

            if (!hourlyVideos[hourKey]) {
                hourlyVideos[hourKey] = [];
            }
            hourlyVideos[hourKey].push(video);
        } catch (error) {
            console.error("Error processing video time:", video.time, error);
        }
    });

    return hourlyVideos;
};

export const useFetchDeviceVideos = ({
    devices,
    selectedDate,
    timezone,
    enabled = true,
}: UseFetchDeviceVideosOptions) => {
    const apiClient = useApiClient();

    const selectedTemporal = selectedDate
        ? toTemporalZonedDateTime(selectedDate, timezone)
        : null;

    // Use Temporal helpers to produce exact ISO strings in UTC for the given
    // timezone-aware start/end of day. Converting the zoned datetime to a
    // plain JS Date and calling toISOString yields a deterministic
    // ``YYYY-MM-DDTHH:mm:ss.sssZ`` value suitable for API queries.
    const startOfDay = selectedTemporal
        ? temporalToDate(startOf(selectedTemporal, "day")).toISOString()
        : null;

    const endOfDay = selectedTemporal
        ? temporalToDate(endOf(selectedTemporal, "day")).toISOString()
        : null;

    const results = useQueries({
        queries: devices.map((device) => ({
            queryKey: [
                "device-videos",
                device.id,
                selectedDate?.toISOString().split("T")[0],
                timezone,
            ],
            queryFn: async ({ signal }: { signal?: AbortSignal }) => {
                const response = await apiClient.get<{ data: Video[] }>(
                    `/devices/${device.id}/videos?startTime=${startOfDay}&endTime=${endOfDay}`,
                    { signal },
                );
                return response.data || [];
            },
            enabled: enabled && !!device.id && !!startOfDay && !!endOfDay,
            staleTime: 2 * 60 * 1000,
            gcTime: 5 * 60 * 1000,
        })),
        combine: (results) => {
            const videosByDevice: Record<string, Record<string, Video[]>> = {};

            results.forEach((result, index) => {
                const device = devices[index];
                if (result.data) {
                    // Aggregate videos by hour for this device
                    videosByDevice[device.id] = aggregateVideosByHour(
                        result.data,
                        timezone,
                    );
                } else {
                    videosByDevice[device.id] = {};
                }
            });

            return {
                data: videosByDevice,
                isPending: results.some((result) => result.isPending),
                isFetching: results.some((result) => result.isFetching),
                error: results.find((result) => result.error)?.error,
                isError: results.some((result) => result.isError),
            };
        },
    });

    return results;
};
